import { Toast } from "primereact/toast";
import {
  type ChangeEvent,
  type DragEvent,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import uploadService, {
  UploadCategory,
} from "../../services/uploadService";
import {
  ActionButton,
  Actions,
  DropHint,
  DropTitle,
  DropZone,
  EmptyState,
  IconBadge,
  Label,
  PreviewFrame,
  PreviewImage,
  ProgressBlock,
  ProgressLabel,
  ProgressTrack,
  ProgressValue,
  UploadContainer,
  UploadingMask,
} from "./styles";

const UploadGlyph = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M12 16V5m0 0L8 9m4-4 4 4"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M5 16.5V18a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-1.5"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ACCEPTED_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);
const ACCEPTED_EXTENSIONS = /\.(jpe?g|png|webp)$/i;

interface ImageUploadProps {
  value: string;
  onChange: (url: string) => void;
  category: UploadCategory;
  contentId?: number | string;
  label?: string;
  required?: boolean;
  onUploadingChange?: (isUploading: boolean) => void;
}

export const ImageUpload = ({
  value,
  onChange,
  category,
  contentId,
  label = "Imagem",
  required = false,
  onUploadingChange,
}: ImageUploadProps) => {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const toast = useRef<Toast>(null);
  const uploadSequence = useRef(0);
  const requestController = useRef<AbortController | null>(null);
  const onUploadingChangeRef = useRef(onUploadingChange);
  const [localPreview, setLocalPreview] = useState("");
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    onUploadingChangeRef.current = onUploadingChange;
  }, [onUploadingChange]);

  useEffect(() => {
    return () => {
      if (localPreview) {
        URL.revokeObjectURL(localPreview);
      }
    };
  }, [localPreview]);

  useEffect(() => {
    return () => {
      uploadSequence.current += 1;
      requestController.current?.abort();
      onUploadingChangeRef.current?.(false);
    };
  }, []);

  const showError = (detail: string) =>
    toast.current?.show({ severity: "error", summary: "Erro", detail, life: 4000 });

  const updateUploading = (isUploading: boolean) => {
    setUploading(isUploading);
    onUploadingChangeRef.current?.(isUploading);
  };

  const upload = async (file: File) => {
    if (
      !ACCEPTED_TYPES.has(file.type) ||
      !ACCEPTED_EXTENSIONS.test(file.name)
    ) {
      showError("Selecione uma imagem JPG, JPEG, PNG ou WEBP.");
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      showError("A imagem deve ter no máximo 5 MB.");
      return;
    }

    const sequence = ++uploadSequence.current;
    const controller = new AbortController();
    requestController.current = controller;
    setLocalPreview(URL.createObjectURL(file));
    updateUploading(true);
    setProgress(0);

    try {
      const response = await uploadService.uploadImage({
        file,
        category,
        contentId,
        signal: controller.signal,
        onProgress: (nextProgress) => {
          if (sequence === uploadSequence.current) {
            setProgress(nextProgress);
          }
        },
      });

      if (sequence !== uploadSequence.current) {
        return;
      }

      const uploadedUrl = response.data?.url?.trim();
      if (!uploadedUrl) {
        throw new Error("A resposta do upload não contém uma URL.");
      }
      const parsedUrl = new URL(uploadedUrl, window.location.origin);
      if (!["http:", "https:"].includes(parsedUrl.protocol)) {
        throw new Error("A resposta do upload contém uma URL inválida.");
      }

      onChange(uploadedUrl);
      setLocalPreview("");
      setProgress(100);
      toast.current?.show({
        severity: "success",
        summary: "Sucesso",
        detail: "Imagem enviada com sucesso.",
        life: 3000,
      });
    } catch {
      if (sequence !== uploadSequence.current || controller.signal.aborted) {
        return;
      }
      showError("Não foi possível enviar a imagem. Tente novamente.");
    } finally {
      if (sequence === uploadSequence.current) {
        requestController.current = null;
        updateUploading(false);
      }
    }
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      void upload(file);
    }
    event.target.value = "";
  };

  const handleDrop = (event: DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setDragging(false);
    const file = event.dataTransfer.files?.[0];
    if (!uploading && file) {
      void upload(file);
    }
  };

  const cancelUpload = () => {
    uploadSequence.current += 1;
    requestController.current?.abort();
    requestController.current = null;
    updateUploading(false);
    setProgress(0);
    setLocalPreview("");
  };

  const removeImage = () => {
    cancelUpload();
    onChange("");
  };

  const preview = localPreview || value;

  return (
    <UploadContainer>
      <Toast ref={toast} />
      {label && (
        <Label htmlFor={inputId}>
          {label}
          {required ? " *" : ""}
        </Label>
      )}
      <input
        id={inputId}
        ref={inputRef}
        type="file"
        accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
        hidden
        disabled={uploading}
        aria-required={required}
        onChange={handleInputChange}
      />
      <DropZone
        type="button"
        isDragging={dragging}
        hasPreview={Boolean(preview)}
        isUploading={uploading}
        onDragEnter={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragOver={(event) => event.preventDefault()}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => !uploading && inputRef.current?.click()}
        disabled={uploading}
      >
        {preview ? (
          <PreviewFrame>
            <PreviewImage src={preview} alt={`Pré-visualização: ${label || "imagem"}`} />
            {uploading && (
              <UploadingMask>Enviando imagem... {progress}%</UploadingMask>
            )}
          </PreviewFrame>
        ) : (
          <EmptyState>
            <IconBadge>
              <UploadGlyph />
            </IconBadge>
            <DropTitle>
              {uploading ? "Enviando imagem..." : "Arraste a imagem aqui"}
            </DropTitle>
            <DropHint>ou clique para selecionar</DropHint>
            <DropHint>JPG, JPEG, PNG ou WEBP · até 5 MB</DropHint>
          </EmptyState>
        )}
      </DropZone>
      {uploading && (
        <ProgressBlock>
          <ProgressTrack
            role="progressbar"
            aria-label="Progresso do upload"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={progress}
          >
            <ProgressValue $progress={progress} />
          </ProgressTrack>
          <ProgressLabel>{progress}%</ProgressLabel>
        </ProgressBlock>
      )}
      {preview && (
        <Actions>
          <ActionButton
            type="button"
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
          >
            Substituir
          </ActionButton>
          <ActionButton
            type="button"
            $danger
            onClick={uploading ? cancelUpload : removeImage}
          >
            {uploading ? "Cancelar envio" : "Remover"}
          </ActionButton>
        </Actions>
      )}
    </UploadContainer>
  );
};
